import { useState } from 'react';
import {
  UseMutationResult,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { APIError, DefaultQueryParams, Language, UserTrialsQueryParams } from '@data';
import {
  chatItemsKeys,
  userTrialContactsKeys,
  userTrialLocationsKeys,
  userTrialsKeys,
  volunteerTrialOverviewKeys,
} from '@data/constants';
import {
  ListContactView,
  ListLocationView,
  ListUserTrialView,
  TrialsOverviewView,
  UpdateMyTrialBody,
  UserTrialDetailView,
  UserTrialFeedBackView,
  UserTrialFeedbackBody,
  UserTrialsApiGetMyTrialLocationsRequest,
  UserTrialStatus,
  HistoryType,
  CurrentFlowView,
} from '@data/sdk/match';
import { MatchingApi } from '@data/utils/axios-instance';
import i18n from '../../../app/i18n';
import { useDebounce } from '../../../app/utils/hooks/useDebounce.hook';

export const useUserTrials = (params: UserTrialsQueryParams) =>
  useQuery<ListUserTrialView, Error>(userTrialsKeys.list(params), async () => {
    const response = await MatchingApi.userTrials.getMyTrials(params);
    return response.data;
  });

export const useUserTrial = (id: string) => {
  return useQuery<UserTrialDetailView, Error>(
    userTrialsKeys.detail(id),
    async () => {
      const response = await MatchingApi.userTrials.getMyTrialDetail({
        id,
        language: i18n.language as Language,
      });
      return response.data;
    },
    {
      enabled: !!id,
    },
  );
};

export const useCreateTrialFeedback = (): UseMutationResult<
  UserTrialFeedBackView,
  APIError<UserTrialFeedbackBody>,
  { id: string; values: UserTrialFeedbackBody },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ id, values }) => {
      const response = await MatchingApi.userTrials.respondWithFeedback({
        id,
        userTrialFeedbackBody: values,
      });
      return response.data;
    },
    {
      onSuccess: (_, { id }) => queryClient.invalidateQueries(userTrialsKeys.detail(id)),
    },
  );
};

export const useStartStopTrialFlow = (): UseMutationResult<
  unknown,
  APIError<UpdateMyTrialBody>,
  { trialId: string; values: UpdateMyTrialBody },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ trialId, values }) => {
      const response = await MatchingApi.userTrials.updateMyTrialFlow({
        id: trialId,
        updateMyTrialBody: values,
      });
      return response.data;
    },
    {
      onSuccess: (_, { trialId }) => {
        queryClient.invalidateQueries(chatItemsKeys.allByHistoryType(HistoryType.All), {
          refetchPage: (__, i) => i === 0,
        });
        queryClient.invalidateQueries(userTrialsKeys.detail(trialId));
      },
    },
  );
};

export const useUserTrialContacts = (id: string, params: DefaultQueryParams, enabled: boolean) =>
  useQuery<ListContactView, Error>(
    userTrialContactsKeys.list(id),
    async () => {
      const response = await MatchingApi.userTrials.getTrialContacts({ id, ...params });
      return response.data;
    },
    { enabled: !!id && !!enabled },
  );

export const useUserTrialsOverview = (enabled = true) => {
  const defaultParams = {
    limit: 15,
    offset: 0,
    language: i18n.language as Language,
  };

  const queryClient = useQueryClient();
  const request = useQuery<TrialsOverviewView, Error>(
    volunteerTrialOverviewKeys.lists(),
    async () => {
      const response = await MatchingApi.userTrials.getTrialsOverview();
      return response.data;
    },
    {
      enabled,
    },
  );
  const eligibleTrials = queryClient.getQueryData(
    userTrialsKeys.allPerStatus(UserTrialStatus.Eligible),
  );

  const activeTrials = queryClient.getQueryData(
    userTrialsKeys.allPerStatus(UserTrialStatus.Active),
  );

  if (request.data?.eligible && !eligibleTrials) {
    queryClient.prefetchInfiniteQuery({
      queryKey: userTrialsKeys.allPerStatus(UserTrialStatus.Eligible),
      queryFn: async ({ pageParam = { ...defaultParams, status: UserTrialStatus.Eligible } }) => {
        const response = await MatchingApi.userTrials.getMyTrials(pageParam);
        return response.data;
      },
    });
  }

  if (request.data?.active && !activeTrials) {
    queryClient.prefetchInfiniteQuery({
      queryKey: userTrialsKeys.allPerStatus(UserTrialStatus.Active),
      queryFn: async ({ pageParam = { ...defaultParams, status: UserTrialStatus.Active } }) => {
        const response = await MatchingApi.userTrials.getMyTrials(pageParam);
        return response.data;
      },
    });
  }

  return request;
};

export const useUserTrialLocationsInfinite = () => {
  const { trialId = '' } = useParams<{ trialId: string }>();
  const [query, setQuery] = useState<UserTrialsApiGetMyTrialLocationsRequest>({
    limit: 20,
    offset: 0,
    searchString: undefined,
    id: trialId,
  });

  const debouncedSearch = useDebounce(query.searchString);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetching,
    isFetchingNextPage,
    isFetchedAfterMount,
  } = useInfiniteQuery<ListLocationView, APIError>({
    queryKey: userTrialLocationsKeys.list({
      ...query,
      searchString: debouncedSearch,
    }),

    queryFn: async ({
      pageParam = {
        ...query,
        searchString: debouncedSearch,
      },
    }) => {
      const response = await MatchingApi.userTrials.getMyTrialLocations(pageParam);
      return response.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      const nextOffset = allPages.reduce((prev, next) => prev + next.items.length, 0);

      return nextOffset < lastPage.count
        ? {
            limit: query.limit,
            offset: nextOffset,
            searchString: debouncedSearch || undefined,
            id: query.id,
          }
        : undefined;
    },

    keepPreviousData: true,
    refetchOnWindowFocus: false,
    cacheTime: 1000 * 60 * 60,
    staleTime: 1000 * 60 * 60,
  });

  const investigationSites = data?.pages.flatMap((page) => page.items) || [];

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery({ ...query, searchString: e.target.value || undefined, offset: 0 });
  };

  const investigationSitesCount = data?.pages[0].count || 0;

  return {
    investigationSitesCount,
    investigationSites,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetching,
    handleSearchChange,
    searchString: query.searchString,
    isFetchingNextPage,
    isFetchedAfterMount,
  };
};

export const useCurrentFlow = () =>
  useQuery<CurrentFlowView, Error>(userTrialContactsKeys.currentFlow(), async () => {
    const response = await MatchingApi.userTrials.getCurrentFlow();
    return response.data;
  });
